export const COACHING_CONFIG_LOCALE_OPTIONS = [
  { label: 'German', value: 'de' },
  { label: 'English', value: 'en' },
];

export const COACHING_CONFIG_PROMPT_MODE_OPTIONS = [
  { label: 'Informed (full interview)', value: 'informed' },
  { label: 'Naive (research / shallow)', value: 'naive' },
];

export const COACHING_CONFIG_PROFILING_MODE_OPTIONS = [
  { label: 'Full interview for all dimensions', value: 'full' },
  { label: 'Skip dimensions with assessment scores', value: 'skip_covered' },
  { label: 'Shortened context-only interview', value: 'shortened' },
];

export const COACHING_CONFIG_REVIEW_POLICY_OPTIONS = [
  { label: 'Off', value: 'off' },
  { label: 'Flagged scores only', value: 'flagged_only' },
  { label: 'Always require review', value: 'always' },
];

export const COACHING_PROMPT_TEMPLATE_OPTIONS = [
  {
    key: 'coaching_opening',
    label: 'Coaching — opening turn',
    placeholders: ['profileContext', 'dimensionTitle', 'mainQuestion'],
  },
  {
    key: 'coaching_engagement',
    label: 'Coaching — follow-up turns',
    placeholders: [
      'profileContext',
      'dimensionTitle',
      'mainQuestion',
      'followUpList',
      'askedList',
      'minEngagementChars',
      'readyMessage',
    ],
  },
  {
    key: 'profiling_informed',
    label: 'Profiling — informed mode',
    placeholders: [
      'dimensionTitle',
      'dimensionLabel',
      'description',
      'greetingInstruction',
      'profileContext',
      'baselineContext',
      'interviewQuestions',
      'completeMarker',
      'minAnswers',
    ],
  },
  {
    key: 'profiling_naive',
    label: 'Profiling — naive mode',
    placeholders: ['dimensionTitle', 'completeMarker'],
  },
  {
    key: 'scoring_informed',
    label: 'Scoring — informed (JSON)',
    placeholders: ['dimensionTitle', 'rubric', 'transcript'],
  },
  {
    key: 'scoring_naive',
    label: 'Scoring — naive (JSON)',
    placeholders: ['dimensionTitle', 'transcript'],
  },
  {
    key: 'dimension_report',
    label: 'Dimension report (markdown)',
    placeholders: ['dimensionTitle', 'mainQuestion', 'userMessages'],
  },
  {
    key: 'profile_summary',
    label: 'Profile summary',
    placeholders: ['scoresFormatted'],
  },
  {
    key: 'assessment_assist_chat',
    label: 'Assessment assist — help chat',
    placeholders: ['progressBlock', 'questionText', 'scaleHint'],
  },
  {
    key: 'assessment_assist_extract',
    label: 'Assessment assist — answer extraction (JSON)',
    placeholders: ['extractTask', 'questionText', 'transcript', 'jsonFormat'],
  },
];

export function createEmptyPromptTemplates(defaults = {}) {
  const templates = {};
  for (const option of COACHING_PROMPT_TEMPLATE_OPTIONS) {
    templates[option.key] = {
      de: defaults[option.key]?.de ?? '',
      en: defaults[option.key]?.en ?? '',
    };
  }
  return templates;
}

export function createEmptyCoachingConfigForm() {
  return {
    defaultLocale: 'de',
    dimensionValues: [],
    profilingModeDefault: 'full',
    reviewPolicy: 'flagged_only',
    promptModeDefault: 'informed',
    persistDimensionReports: true,
    bootstrapSchemaId: null,
    dimensionCompleteMarker: '[DIMENSION_COMPLETE]',
    coachingReadyMessage: { de: '', en: '' },
    minEngagementChars: 50,
    minProfilingAnswersBeforeComplete: 2,
    systemPromptPrefix: { de: '', en: '' },
    promptTemplates: createEmptyPromptTemplates(),
  };
}

export function normalizeCoachingConfigForm(data, promptTemplateDefaults = null) {
  const base = createEmptyCoachingConfigForm();
  const mergedDefaults = promptTemplateDefaults
    ? createEmptyPromptTemplates(promptTemplateDefaults)
    : base.promptTemplates;

  const incomingTemplates = data?.promptTemplates ?? {};
  const promptTemplates = createEmptyPromptTemplates();
  for (const option of COACHING_PROMPT_TEMPLATE_OPTIONS) {
    promptTemplates[option.key] = {
      de: incomingTemplates[option.key]?.de ?? mergedDefaults[option.key]?.de ?? '',
      en: incomingTemplates[option.key]?.en ?? mergedDefaults[option.key]?.en ?? '',
    };
  }

  return {
    ...base,
    ...data,
    dimensionValues: Array.isArray(data?.dimensionValues) ? [...data.dimensionValues] : [],
    bootstrapSchemaId: data?.bootstrapSchemaId || null,
    coachingReadyMessage: {
      de: data?.coachingReadyMessage?.de ?? base.coachingReadyMessage.de,
      en: data?.coachingReadyMessage?.en ?? base.coachingReadyMessage.en,
    },
    systemPromptPrefix: {
      de: data?.systemPromptPrefix?.de ?? '',
      en: data?.systemPromptPrefix?.en ?? '',
    },
    promptTemplates,
  };
}

export function mapDimensionOptions(dimensions = []) {
  return dimensions.map((dimension) => ({
    label: dimension.translation?.de || dimension.translation?.en || dimension.value,
    value: dimension.value,
  }));
}

export function mapSchemaOptions(schemas = []) {
  return schemas.map((schema) => ({
    label: schema.name || schema.schemaName || schema._id,
    value: schema._id,
  }));
}
